import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, publishReplay, refCount, switchMap, take } from 'rxjs/operators';
import { EditPartnerIdDialogService } from './edit-partner-id-dialog.service';
import { ITEM_COMMON_MODULE_IMPORTS } from '@galaxy/inventory-ui';
import { FormsModule } from '@angular/forms';
import { MODULE_IMPORTS } from '@vendasta/galaxy/loading-spinner';
import { GetPartnerAccountGroupMappingResponseInterface } from '@vendasta/partner';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-edit-partner-id',
  templateUrl: './edit-partner-id-dialog.component.html',
  styleUrls: ['./edit-partner-id-dialog.component.scss'],
  imports: [ITEM_COMMON_MODULE_IMPORTS, FormsModule, MODULE_IMPORTS, RouterLink],
  providers: [EditPartnerIdDialogService],
})
export class EditPartnerIdDialogComponent {
  dialogConfig: MatDialogConfig;
  newPartnerID = '';
  stepOne = true;
  validPID = true;
  saving = false;
  loadingStepTwoData = false;
  stepTwoPageData$: Observable<{
    currentPID: string;
    currentAGID: string;
    currentName: string;
    withPID: string;
    withAGID: string;
    withName: string;
    isSwapping: boolean;
  }>;

  private readonly editPartnerIdService = inject(EditPartnerIdDialogService);
  private readonly dialogRef = inject(MatDialogRef<EditPartnerIdDialogComponent>);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translateService = inject(TranslateService);
  public data = inject(MAT_DIALOG_DATA);

  pidUpdated(): void {
    this.validPID = true;
  }

  next(): void {
    if (this.newPartnerID === this.data.pid) {
      this.snackbarService.openErrorSnack(
        'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.EDIT_PARTNER_ID.PARTNER_ID_ERROR_SAME_PID',
      );
      return;
    }
    this.loadingStepTwoData = true;
    this.stepOne = false;
    this.getStepTwoPageData(this.newPartnerID);
  }

  getStepTwoPageData(pid: string): void {
    const partnerAccountGroup$ = this.editPartnerIdService.getAccountGroupForPID(pid).pipe(
      take(1),
      catchError(() => {
        this.validPID = false;
        this.loadingStepTwoError('');
        return of(null);
      }),
      publishReplay(1),
      refCount(),
    );

    const newAccountGroupName$: Observable<string> = partnerAccountGroup$.pipe(
      take(1),
      switchMap((pag: GetPartnerAccountGroupMappingResponseInterface) => {
        if (!pag) {
          return of('');
        }
        return this.editPartnerIdService.getCompanyName(pag.accountGroupId);
      }),
    );

    partnerAccountGroup$
      .pipe(
        take(1),
        switchMap((pag: GetPartnerAccountGroupMappingResponseInterface) => {
          // If pag is null, it means there was an error getting the partner account group, return true.
          if (!pag) {
            return of(true);
          }
          return of(false);
        }),
      )
      .subscribe((failed) => {
        if (failed) {
          this.loadingStepTwoError(
            this.translateService.instant(
              'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.EDIT_PARTNER_ID.GET_BUSINESS_NAME_ERROR',
            ),
          );
        }
      });

    this.stepTwoPageData$ = combineLatest([partnerAccountGroup$, newAccountGroupName$]).pipe(
      take(1),
      map(([pag, newName]) => {
        this.loadingStepTwoData = false;
        if (!!pag && !!newName) {
          return {
            currentPID: this.data.pid,
            currentAGID: this.data.accountGroupId,
            currentName: this.data.name,
            withPID: pag.partnerId,
            withAGID: pag.accountGroupId,
            withName: newName,
            isSwapping: !!this.data.pid, // Only if the business is currently mapped is it a swap
          };
        }
      }),
    );
  }

  loadingStepTwoError(errorMessage: string): void {
    this.stepOne = true;
    this.loadingStepTwoData = false;
    if (errorMessage !== '') {
      this.snackbarService.openErrorSnack(errorMessage);
    }
  }

  save(isSwap: boolean, currentPID: string, currentAGID: string, withPID: string, withAGID: string): void {
    this.saving = true;
    if (isSwap) {
      this.swapAccounts(currentPID, currentAGID, withPID, withAGID);
    } else {
      this.updateAccount(withPID, currentAGID);
    }
  }

  swapAccounts(partnerId: string, accountGroupId: string, withPartnerId: string, withAccountGroupId: string): void {
    this.editPartnerIdService
      .swapAccountGroup(partnerId, accountGroupId, withPartnerId, withAccountGroupId)
      .subscribe((response) => {
        if (response.success) {
          this.snackbarService.openSuccessSnack(
            'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.EDIT_PARTNER_ID.SAVE_PARTNER_ID_SUCCESS',
          );
          this.close(true);
        } else {
          const errorMessage =
            response.error || 'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.EDIT_PARTNER_ID.SAVE_PARTNER_ID_ERROR';
          this.snackbarService.openErrorSnack(errorMessage);
        }
        this.saving = false;
      });
  }

  updateAccount(partnerId: string, accountGroupId: string): void {
    this.editPartnerIdService.updateAccountGroup(partnerId, accountGroupId).subscribe((response) => {
      if (response.success) {
        this.snackbarService.openSuccessSnack(
          'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.EDIT_PARTNER_ID.SAVE_PARTNER_ID_SUCCESS',
        );
        this.close(true);
      } else {
        const errorMessage =
          response.error || 'CRM.ASSOCIATION_PANEL.PARTNER_INFORMATION.EDIT_PARTNER_ID.SAVE_PARTNER_ID_ERROR';
        this.snackbarService.openErrorSnack(errorMessage);
      }
      this.saving = false;
    });
  }

  close(updateSuccess: boolean): void {
    const newPID = updateSuccess ? this.newPartnerID : null;
    this.dialogRef.close(newPID);
  }
}
